import React from "react";
import { Link as RouterLink } from "react-router-dom";
import {
  Container,
  VStack,
  Heading,
  Text,
  Box,
  useColorMode,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Badge,
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
} from "@chakra-ui/react";
import { brandConfig, getColor } from "../../brandConfig";
import { NavbarWithCallToAction } from "../../components/chakra/NavbarWithCallToAction/NavbarWithCallToAction";
import { FooterWithFourColumns } from "../../components/chakra/FooterWithFourColumns/FooterWithFourColumns";

interface Refinery {
  name: string;
  country: string;
  capacity: string;
  owner: string;
  suppliesTo: string;
  relevance: string;
  hormuzRisk: "none" | "low" | "medium" | "high";
}

const refineries: Refinery[] = [
  {
    name: "S-Oil (Onsan)",
    country: "South Korea",
    capacity: "669,000 bpd",
    owner: "Saudi Aramco (63%)",
    suppliesTo: "Japan, Australia, SE Asia",
    relevance: "Closest large refinery to Japan, existing supply relationship",
    hormuzRisk: "high",
  },
  {
    name: "GS Caltex (Yeosu)",
    country: "South Korea",
    capacity: "800,000 bpd",
    owner: "GS Energy / Chevron JV",
    suppliesTo: "Japan, Australia, Pacific",
    relevance: "Already supplying both Japan and Australia with refined products",
    hormuzRisk: "medium",
  },
  {
    name: "Formosa Petrochemical (Mailiao)",
    country: "Taiwan",
    capacity: "540,000 bpd",
    owner: "Formosa Plastics Group",
    suppliesTo: "Taiwan, Japan, regional",
    relevance: "Close proximity to Japan, large capacity, diversified crude sources",
    hormuzRisk: "medium",
  },
  {
    name: "Hengli Petrochemical",
    country: "China",
    capacity: "400,000 bpd",
    owner: "Hengli Group",
    suppliesTo: "China domestic, regional export",
    relevance: "Physically close but political complexity for JP/AU supply",
    hormuzRisk: "medium",
  },
  {
    name: "Viva Energy (Geelong)",
    country: "Australia",
    capacity: "120,000 bpd",
    owner: "Viva Energy Group",
    suppliesTo: "VIC, TAS, SA domestic",
    relevance: "One of only 2 remaining AU refineries, could supply Japan in emergency",
    hormuzRisk: "none",
  },
  {
    name: "Ampol (Lytton)",
    country: "Australia",
    capacity: "109,000 bpd",
    owner: "Ampol Limited",
    suppliesTo: "QLD domestic",
    relevance: "QLD's only refinery, supplies ~60% of state fuel, critical infrastructure",
    hormuzRisk: "none",
  },
  {
    name: "ExxonMobil (Beaumont)",
    country: "USA (Texas)",
    capacity: "400,000+ bpd",
    owner: "ExxonMobil",
    suppliesTo: "US, Pacific exports",
    relevance: "Pacific tanker route viable to JP/AU, no chokepoint risk",
    hormuzRisk: "none",
  },
  {
    name: "Phillips 66 (Ferndale)",
    country: "USA (Washington)",
    capacity: "105,000 bpd",
    owner: "Phillips 66",
    suppliesTo: "US Pacific NW, Pacific exports",
    relevance: "Closest US refinery to Japan via Pacific route (~10 days transit)",
    hormuzRisk: "none",
  },
];

const riskColor = (risk: string) => {
  switch (risk) {
    case "high": return "red";
    case "medium": return "orange";
    case "low": return "yellow";
    case "none": return "green";
    default: return "gray";
  }
};

const KeyRefineries: React.FC = () => {
  const { colorMode } = useColorMode();
  const bgMain = getColor("background.main", colorMode);
  const textPrimary = getColor("text.primary", colorMode);
  const surfaceBg = brandConfig.colors.background.surface;
  const borderLight = brandConfig.colors.border.light;
  const textSecondary = brandConfig.colors.text.secondary;

  return (
    <Box bg={bgMain} minHeight="100vh" display="flex" flexDirection="column">
      <NavbarWithCallToAction />

      <Container maxW="container.xl" py={10} flex="1">
        <VStack spacing={6} align="stretch">
          <Breadcrumb fontSize="sm" color={textSecondary}>
            <BreadcrumbItem>
              <BreadcrumbLink as={RouterLink} to="/energy-research">
                Energy Research
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbItem isCurrentPage>
              <BreadcrumbLink>Key Refineries</BreadcrumbLink>
            </BreadcrumbItem>
          </Breadcrumb>

          <Heading size="lg" color="#dc2626">
            Key Refineries (Global)
          </Heading>
          <Text color={textSecondary} fontSize="sm">
            The refineries that matter most for Australia and Japan's fuel supply -- capacity, ownership,
            and Hormuz risk if the strait is blocked
          </Text>

          <Box
            overflowX="auto"
            bg={surfaceBg}
            borderRadius="lg"
            border="1px solid"
            borderColor={borderLight}
          >
            <Table size="sm">
              <Thead>
                <Tr>
                  <Th color={textSecondary}>Refinery</Th>
                  <Th color={textSecondary}>Country</Th>
                  <Th color={textSecondary}>Capacity</Th>
                  <Th color={textSecondary}>Owner</Th>
                  <Th color={textSecondary}>Supplies To</Th>
                  <Th color={textSecondary}>Hormuz Risk</Th>
                </Tr>
              </Thead>
              <Tbody>
                {refineries.map((r) => (
                  <Tr key={r.name}>
                    <Td color={textPrimary} fontWeight="bold">{r.name}</Td>
                    <Td color={textPrimary}>{r.country}</Td>
                    <Td color={textPrimary}>{r.capacity}</Td>
                    <Td color={textPrimary} fontSize="xs">{r.owner}</Td>
                    <Td color={textPrimary} fontSize="xs">{r.suppliesTo}</Td>
                    <Td>
                      <Badge colorScheme={riskColor(r.hormuzRisk)} fontSize="2xs">
                        {r.hormuzRisk === "none" ? "No risk" : r.hormuzRisk}
                      </Badge>
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </Box>

          {/* Relevance notes */}
          <Box p={6} bg={surfaceBg} borderRadius="lg" border="1px solid" borderColor={borderLight}>
            <Heading size="sm" color="#dc2626" mb={3}>
              Why These Refineries Matter
            </Heading>
            <VStack align="start" spacing={2}>
              {refineries.map((r) => (
                <Text key={r.name} fontSize="sm" color={textPrimary}>
                  <strong>{r.name}:</strong> {r.relevance}
                </Text>
              ))}
            </VStack>
          </Box>
        </VStack>
      </Container>

      <FooterWithFourColumns />
    </Box>
  );
};

export default KeyRefineries;
